import { useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Space, Tag, Modal, message } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { DataGrid, type DataGridHandle } from '@/components/DataGrid';
import type { SearchParams } from '@/api/types';
import {
  getAuditConfigs,
  deleteAuditConfig,
  type AuditConfig,
} from '@/api/auditApi';
import { useAuth } from '@/hooks/useAuth';
import AuditNav from './AuditNav';

export default function AuditConfigListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const gridRef = useRef<DataGridHandle>(null);

  const canWrite = hasPermission('audit', 'write');
  const canDelete = hasPermission('audit', 'delete');

  const fetchData = useCallback(
    (params: SearchParams) => getAuditConfigs(params),
    []
  );

  const handleDelete = useCallback(
    (id: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('audit.confirm_delete_config'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        okType: 'danger',
        onOk: async () => {
          try {
            await deleteAuditConfig(id);
            message.success(t('audit.config_delete_success'));
            gridRef.current?.reload();
          } catch {
            message.error(t('app.error'));
          }
        },
      });
    },
    [t]
  );

  const columns: ColumnsType<AuditConfig> = useMemo(
    () => [
      {
        title: t('audit.field.resource'),
        dataIndex: 'resource',
        key: 'resource',
        sorter: true,
        ellipsis: true,
      },
      {
        title: t('audit.field.actions'),
        dataIndex: 'actions',
        key: 'actions',
        width: 200,
        render: (actions: string[]) =>
          actions.map((a) => (
            <Tag key={a} color="blue">
              {t(`audit.action_options.${a}`, a)}
            </Tag>
          )),
      },
      {
        title: t('audit.field.is_enabled'),
        dataIndex: 'is_enabled',
        key: 'is_enabled',
        width: 100,
        render: (enabled: boolean) => (
          <Tag color={enabled ? 'green' : 'default'}>
            {enabled ? t('app.yes') : t('app.no')}
          </Tag>
        ),
      },
      {
        title: t('audit.field.retention_days'),
        dataIndex: 'retention_days',
        key: 'retention_days',
        width: 120,
        sorter: true,
      },
      {
        title: t('audit.field.log_details'),
        dataIndex: 'log_details',
        key: 'log_details',
        width: 100,
        render: (v: boolean) => (
          <Tag color={v ? 'green' : 'default'}>
            {v ? t('app.yes') : t('app.no')}
          </Tag>
        ),
      },
      {
        title: t('audit.field.notify_on_action'),
        dataIndex: 'notify_on_action',
        key: 'notify_on_action',
        width: 120,
        render: (v: boolean) => (
          <Tag color={v ? 'orange' : 'default'}>
            {v ? t('app.yes') : t('app.no')}
          </Tag>
        ),
      },
      {
        title: t('app.actions'),
        key: 'col_actions',
        width: 120,
        render: (_: unknown, record: AuditConfig) => (
          <Space size="small">
            {canWrite && (
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => navigate(`/audit/configs/${record.id}/edit`)}
              />
            )}
            {canDelete && (
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record.id)}
              />
            )}
          </Space>
        ),
      },
    ],
    [t, navigate, canWrite, canDelete, handleDelete]
  );

  const toolbar = (
    <Space>
      {canWrite && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/audit/configs/new')}
        >
          {t('audit.new_config')}
        </Button>
      )}
    </Space>
  );

  return (
    <div>
      <AuditNav />
      <h2>{t('audit.config_list_title')}</h2>
      <DataGrid<AuditConfig>
        ref={gridRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
        searchable
        searchPlaceholder={t('audit.search_placeholder')}
        defaultSortField="created_at"
        defaultSortDescending
        toolbar={toolbar}
      />
    </div>
  );
}
